import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import rrulePlugin from "@fullcalendar/rrule";
import { EventInput } from "@fullcalendar/core";
import { Box, Grid, Typography, RadioGroup, FormControlLabel, Radio, Modal } from "@mui/material";
import DashboardLayout from "../../layout/DashboardLayout/DashboardLayout";
import { useRecoilState } from "recoil";
import { userAuthState } from "../../atom";
import {
  useUserDriverAvailabilities,
  useOrganizationDriverAvailabilities,
  getUserDriverAvailabilities,
  getOrganizationDriverAvailabilities,
  useCreateAvailabilitySlot,
} from "../../hooks/useDriverAvailability";
import BreadcrumbSection from "../../components/BreadcrumbSection/BreadcrumbSection";
import OrganizationAutoComplete from "../../components/OrganizationAutoComplete/OrganizationAutoComplete";
import { useAdminDetails } from "../../hooks/useAdminUser";
import { mockAvailability } from "../../mocks/mockAvailability";
import DriverDetailModal from "../../components/DriverDetailModal/DriverDetailModal";
import MultiDriverModal from "../../components/MultiDriversModal/MultiDriversModal";
import { User } from "../../types/user";
import { OrganizationDriverAvailabilitySlot } from "../../types/availability";
import { differenceInMinutes, isBefore, isAfter, parseISO } from "date-fns";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { isCurrentAdminFromRootOrganization } from "../../helpers/authHelpers";

const USE_MOCK_DATA = false;

const DriverAvailability = () => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [multipleModalOpen, setMultipleModalOpen] = useState(false);
  const [selectedMultipleUsers, setMultipleUsers] = useState<User[]>([]);
  const [multipleModalSlots, setMultipleModalSlots] = useState<OrganizationDriverAvailabilitySlot[]>([]);

  const [authDetails] = useRecoilState(userAuthState);
  const { data: adminUserData } = useAdminDetails(authDetails?.accessToken);

  const [organizationSearchText, setOrganizationSearchText] = useState(adminUserData?.organization?.name ?? "");
  const [currentOrganization, setCurrentOrganization] = useState(adminUserData?.organization || null);

  const { userId } = useParams<{ userId?: string }>();
  const organizationId = currentOrganization?.id ?? authDetails.organizationId;
  const hasValidUserId = typeof userId === "string" && userId.trim().length > 0;

  const { data: driverAvailabilitiesFromApi = [] } = useQuery({
    queryKey: hasValidUserId
      ? ["user-driver-availabilities", organizationId, userId]
      : ["organization-driver-availabilities", organizationId],
    queryFn: () =>
      hasValidUserId
        ? getUserDriverAvailabilities({
            accessToken: authDetails.accessToken,
            organizationId,
            userId: userId!,
          })
        : getOrganizationDriverAvailabilities({
            accessToken: authDetails.accessToken,
            organizationId,
          }),
    enabled: Boolean(authDetails.accessToken && organizationId),
  });

  const driverAvailabilities = USE_MOCK_DATA ? mockAvailability : driverAvailabilitiesFromApi;

  const createSlotMutation = useCreateAvailabilitySlot();

  // Create slot modal state
  const [createStartTime, setCreateStartTime] = useState<Date | null>(null);
  const [createEndTime, setCreateEndTime] = useState<Date | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<"DAILY" | "WEEKLY" | "MONTHLY">("WEEKLY");
  const [selectedSlot, setSelectedSlot] = useState<OrganizationDriverAvailabilitySlot | null>(null);

  function convertToRRuleEvent(slot: OrganizationDriverAvailabilitySlot): EventInput {
    if (!slot.recurrence || !slot.isRecurring) {
      return {
        title: `${slot.user?.firstName ?? "Driver"} ${slot.user?.lastName ?? ""}`,
        start: slot.startTime,
        end: slot.endTime,
      };
    }

    const { frequency, interval, count, endDate } = slot.recurrence;

    return {
      title: `${slot.user?.firstName ?? "Driver"} ${slot.user?.lastName ?? ""}`,
      rrule: {
        freq: frequency.toLowerCase(),
        dtstart: slot.startTime,
        interval: interval || 1,
        ...(count ? { count } : {}),
        ...(endDate ? { until: endDate } : {}),
      },
      duration: dayjs(slot.endTime).diff(dayjs(slot.startTime), "minute") + "m",
    };
  }

  function groupOverlappingSlots(slots: OrganizationDriverAvailabilitySlot[]) {
    const sorted = [...slots].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    const groups: OrganizationDriverAvailabilitySlot[][] = [];

    sorted.forEach((slot) => {
      const addedToGroup = groups.find((group) =>
        group.some(
          (other) =>
            isBefore(parseISO(slot.startTime), parseISO(other.endTime)) &&
            isAfter(parseISO(slot.endTime), parseISO(other.startTime))
        )
      );
      if (addedToGroup) {
        addedToGroup.push(slot);
      } else {
        groups.push([slot]);
      }
    });

    return groups;
  }
const mappedEvents: EventInput[] = groupOverlappingSlots(driverAvailabilities || [])
  .map((group, idx) => {
    if (group.length >= 4) {
      const start = group.reduce((min, slot) =>
        isBefore(parseISO(slot.startTime), parseISO(min.startTime)) ? slot : min
      ).startTime;
      const end = group.reduce((max, slot) =>
        isAfter(parseISO(slot.endTime), parseISO(max.endTime)) ? slot : max
      ).endTime;

      return [
        {
          id: `multi-${idx}`,
          title: "Multiple Drivers",
          start,
          end,
          extendedProps: {
            type: "multiple" as const,
            users: group.map((s) => s.user),
            slots: group,
          },
        } satisfies EventInput,
      ];
    } else {
      return group.map((slot) => {
        const base = convertToRRuleEvent(slot);
        return {
          ...base,
          extendedProps: {
            type: "single" as const,
            user: slot.user,
            slot,
          },
        } satisfies EventInput;
      });
    }
  })
  .flat();

  useEffect(() => {
    if (adminUserData?.organization) {
      setOrganizationSearchText(adminUserData.organization.name);
      setCurrentOrganization(adminUserData.organization);
    }
  }, [adminUserData]);

  return (
    <DashboardLayout>
      <BreadcrumbSection
        crumbs={[
          { url: "/dashboard", title: "Dashboard" },
          { url: "/dashboard/driver-availability", title: "Driver Availability" },
        ]}
        pageTitle="Driver Availability"
      />

      <Grid container justifyContent={"flex-end"} alignItems={"center"}>
        {isCurrentAdminFromRootOrganization(authDetails) && (
          <Grid item xs={3}>
            <OrganizationAutoComplete
              placeholder={`${adminUserData?.organization?.name} - orgID: ${adminUserData?.organization?.id}`}
              defaultValue={currentOrganization}
              inputValue={organizationSearchText}
              setInputValue={setOrganizationSearchText}
              value={currentOrganization}
              setValue={setCurrentOrganization}
              accessToken={authDetails.accessToken}
            />
          </Grid>
        )}
      </Grid>

      <Grid container px={3} py={4}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin]}
          initialView="timeGridWeek"
          editable={false}
          selectable={true}
          slotDuration="01:00:00"
          events={mappedEvents}
          height="auto"
          eventClick={(info) => {
            const { type, user, slot, users } = info.event.extendedProps;
            if (type === "single") {
              setSelectedUser(user);
              setSelectedSlot(slot);
              setIsModalOpen(true);
            } else if (type === "multiple") {
              setMultipleUsers(users);
              setMultipleModalSlots(info.event.extendedProps.slots);
              setMultipleModalOpen(true);
            }
        }}
          select={(selectInfo) => {
            setCreateStartTime(new Date(selectInfo.startStr));
            setCreateEndTime(new Date(selectInfo.endStr));
          }}
        />
      </Grid>

      <DriverDetailModal
        slot={selectedSlot}
        user={selectedUser}
        open={isModalOpen}
        onClose={() => {
          setSelectedUser(null);
          setIsModalOpen(false);
          setSelectedSlot(null);
        }}
        accessToken={authDetails.accessToken}

      />

      <MultiDriverModal
        users={selectedMultipleUsers}
        slots={multipleModalSlots}
        open={multipleModalOpen}
        onClose={() => {
          setMultipleUsers([]);
          setMultipleModalSlots([]);
          setMultipleModalOpen(false);
        }}
        onSelectUser={(user: User, slot: OrganizationDriverAvailabilitySlot | undefined) => {
          setSelectedUser(user);
          setSelectedSlot(slot ?? null);
          setIsModalOpen(true);
          setMultipleModalOpen(false);
        }}
      />    
      </DashboardLayout>
  );
};

export default DriverAvailability;
